/**
 * One lease: terms, the tenant roster, checklists, and the Michigan compliance
 * clocks. This is the hub — LeaseWizard only creates.
 *
 * "Mark sent" never generates the notice document. Templates are
 * attorney-reviewed (invariant 7); staff attach whatever they generated
 * outside the app through the Files section below, same as any other document.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useInvalidate, useList, useOne } from '@refinedev/core';
import { CHARGE_TYPES, LEASE_STATUSES, NOTICE_KINDS } from 'shared';
import { api } from '../../../lib/api.js';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { humanizeEnum } from '../../../components/Form.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { PhotoUpload } from '../../../components/PhotoUpload.js';
import { useCan } from '../../../lib/capabilities.js';
import { STATUS_PILL_CLASS } from './LeaseList.js';

/** Statuses that mean a unit is actually occupied under this lease. */
const OCCUPIED_STATUSES = new Set(['ACTIVE', 'MONTH_TO_MONTH']);

interface LeaseTenant {
  id: string;
  tenantId: string;
  name: string;
  isPrimary: boolean;
  movedInOn: string | null;
  removedOn: string | null;
}

interface LeaseRecord {
  id: string;
  unitId: string;
  unitLabel: string;
  propertyId: string;
  propertyName: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  monthlyRent: string;
  securityDeposit: string;
  rentDueDay: number;
  depositReceivedOn: string | null;
  moveOutDate: string | null;
  tenants: LeaseTenant[];
  notes: string | null;
}

interface TenantOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface ChecklistRecord {
  id: string;
  kind: string;
  inspectedOn: string | null;
  tenantPresent: boolean;
  tenantReturnedOn: string | null;
  items: unknown[];
}

interface NoticeRecord {
  id: string;
  kind: string;
  triggeredOn: string | null;
  dueBy: string | null;
  sentAt: string | null;
  sentVia: string | null;
  documentId: string | null;
  daysUntilDue: number;
  isOverdue: boolean;
}

interface ChargeRecord {
  id: string;
  chargeType: string;
  amount: string;
  dueDate: string | null;
  description: string | null;
  balance: string;
  isPaid: boolean;
  daysLate: number;
  voidedAt: string | null;
}

interface AllocationRecord {
  id: string;
  chargeId: string;
  amount: string;
  reversedAt: string | null;
}

interface PaymentRecord {
  id: string;
  method: string;
  amount: string;
  status: string;
  receivedOn: string | null;
  allocations: AllocationRecord[];
  allocatedTotal: string;
}

const AUTOMATIC_NOTICE_KINDS = new Set(['DEPOSIT_NOTICE', 'DAMAGES_NOTICE']);
/** Manual entry only — ACH arrives through the Stripe webhook path, once built. */
const MANUAL_PAYMENT_METHODS = ['CHECK', 'CASH', 'MONEY_ORDER', 'OTHER'];

export function LeaseShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const canWrite = useCan('leases:write');
  const canWriteLedger = useCan('ledger:write');
  const canWriteDocuments = useCan('documents:write');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: leaseData, isLoading } = useOne<LeaseRecord>({
    resource: 'leases',
    id: id ?? '',
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: checklistsData, isLoading: checklistsLoading } = useList<ChecklistRecord>({
    resource: 'checklists',
    filters: [{ field: 'leaseId', operator: 'eq', value: id }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: noticesData, isLoading: noticesLoading } = useList<NoticeRecord>({
    resource: 'notices',
    filters: [{ field: 'leaseId', operator: 'eq', value: id }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: tenantsData } = useList<TenantOption>({
    resource: 'tenants',
    pagination: { current: 1, pageSize: 200 },
  });

  const { data: chargesData, isLoading: chargesLoading } = useList<ChargeRecord>({
    resource: 'charges',
    filters: [{ field: 'leaseId', operator: 'eq', value: id }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: paymentsData, isLoading: paymentsLoading } = useList<PaymentRecord>({
    resource: 'payments',
    filters: [{ field: 'leaseId', operator: 'eq', value: id }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(id) },
  });

  const lease = leaseData?.data;

  async function refresh() {
    await invalidate({
      resource: 'leases',
      invalidates: ['detail', 'list'],
      ...(id ? { id } : {}),
    });
    await invalidate({ resource: 'checklists', invalidates: ['list'] });
    await invalidate({ resource: 'notices', invalidates: ['list'] });
    await invalidate({ resource: 'charges', invalidates: ['list'] });
    await invalidate({ resource: 'payments', invalidates: ['list'] });
    // Generating a notice writes a Document row (see generateNotice below) —
    // without this, the Files card keeps showing "No files yet" until the
    // next full navigation.
    await invalidate({ resource: 'documents', invalidates: ['list'] });
  }

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That action failed.');
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <p className="muted">Loading…</p>;
  if (!lease) return <p className="alert alert--error">Lease not found.</p>;

  const namedTenantIds = new Set(lease.tenants.filter((t) => !t.removedOn).map((t) => t.tenantId));
  const addableTenants = (tenantsData?.data ?? []).filter((t) => !namedTenantIds.has(t.id));

  function changeStatus(status: string) {
    if (status === lease?.status) return;
    void run(() =>
      api(`/leases/${id ?? ''}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    );
  }

  function recordDeposit() {
    const today = new Date().toISOString().slice(0, 10);
    const value = window.prompt('Date the deposit was received (YYYY-MM-DD):', today);
    if (!value) return;
    void run(() =>
      api(`/leases/${id ?? ''}/deposit-received`, {
        method: 'POST',
        body: JSON.stringify({ depositReceivedOn: value }),
      }),
    );
  }

  function addTenant(tenantId: string) {
    if (!tenantId) return;
    void run(() =>
      api(`/leases/${id ?? ''}/tenants`, { method: 'POST', body: JSON.stringify({ tenantId }) }),
    );
  }

  function removeTenant(tenant: LeaseTenant) {
    const today = new Date().toISOString().slice(0, 10);
    const value = window.prompt(`Date ${tenant.name} left the lease (YYYY-MM-DD):`, today);
    if (!value) return;
    void run(() =>
      api(`/leases/${id ?? ''}/tenants/${tenant.tenantId}`, {
        method: 'DELETE',
        body: JSON.stringify({ removedOn: value }),
      }),
    );
  }

  function setPrimary(tenant: LeaseTenant) {
    void run(() =>
      api(`/leases/${id ?? ''}/tenants/${tenant.tenantId}/primary`, { method: 'PATCH' }),
    );
  }

  function recordChecklistReturn(checklist: ChecklistRecord) {
    const today = new Date().toISOString().slice(0, 10);
    const value = window.prompt('Date the tenant returned their signed copy (YYYY-MM-DD):', today);
    if (!value) return;
    void run(() =>
      api(`/checklists/${checklist.id}/return`, {
        method: 'PATCH',
        body: JSON.stringify({ tenantReturnedOn: value }),
      }),
    );
  }

  function markNoticeSent(notice: NoticeRecord) {
    const sentVia = window.prompt('How was this notice sent?', 'first-class mail');
    if (!sentVia) return;
    void run(() =>
      api(`/notices/${notice.id}/sent`, { method: 'PATCH', body: JSON.stringify({ sentVia }) }),
    );
  }

  function generateNotice(notice: NoticeRecord) {
    if (
      !window.confirm(
        'This drafts a PLACEHOLDER PDF — not attorney-reviewed — and emails it to every ' +
          'current tenant on this lease. Use it to test the send mechanism, not to send a ' +
          'real notice. Continue?',
      )
    ) {
      return;
    }
    void run(() => api(`/notices/${notice.id}/generate`, { method: 'POST' }));
  }

  async function viewNoticeDocument(documentId: string) {
    try {
      const { downloadUrl } = await api<{ downloadUrl: string }>(
        `/documents/${documentId}/download`,
      );
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open that document.');
    }
  }

  function voidCharge(charge: ChargeRecord) {
    const reason = window.prompt(
      `Void the ${humanizeEnum(charge.chargeType).toLowerCase()} charge for $${charge.amount}? Reason:`,
      '',
    );
    if (reason === null) return;
    void run(() =>
      api(`/charges/${charge.id}/void`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || null }),
      }),
    );
  }

  const openCharges = (chargesData?.data ?? []).filter((c) => !c.voidedAt && !c.isPaid);

  const tenantColumns: Column<LeaseTenant>[] = [
    {
      key: 'name',
      header: 'Tenant',
      render: (row) => (
        <>
          {row.name}
          {row.isPrimary ? <span className="pill"> Primary</span> : null}
          {row.removedOn ? <span className="pill"> Left {row.removedOn}</span> : null}
        </>
      ),
    },
    { key: 'movedIn', header: 'Moved in', render: (row) => row.movedInOn ?? '—' },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        canWrite && !row.removedOn ? (
          <div className="row">
            {!row.isPrimary ? (
              <button
                type="button"
                className="button button--quiet"
                disabled={busy}
                onClick={() => setPrimary(row)}
              >
                Make primary
              </button>
            ) : null}
            {lease.tenants.filter((t) => !t.removedOn).length > 1 ? (
              <button
                type="button"
                className="button button--quiet"
                disabled={busy}
                onClick={() => removeTenant(row)}
              >
                Remove
              </button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  const checklistColumns: Column<ChecklistRecord>[] = [
    { key: 'kind', header: 'Kind', render: (row) => humanizeEnum(row.kind) },
    { key: 'inspectedOn', header: 'Inspected', render: (row) => row.inspectedOn ?? '—' },
    {
      key: 'present',
      header: 'Tenant present',
      render: (row) => (row.tenantPresent ? 'Yes' : 'No'),
    },
    { key: 'items', header: 'Items', numeric: true, render: (row) => row.items.length },
    {
      key: 'returned',
      header: 'Signed copy returned',
      render: (row) => {
        if (row.kind !== 'MOVE_IN') return '—';
        if (row.tenantReturnedOn) return row.tenantReturnedOn;
        return canWrite ? (
          <button
            type="button"
            className="button button--quiet"
            disabled={busy}
            onClick={() => recordChecklistReturn(row)}
          >
            Record return
          </button>
        ) : (
          'Not yet'
        );
      },
    },
  ];

  const noticeColumns: Column<NoticeRecord>[] = [
    { key: 'kind', header: 'Kind', render: (row) => humanizeEnum(row.kind) },
    { key: 'triggeredOn', header: 'Triggered', render: (row) => row.triggeredOn ?? '—' },
    {
      key: 'dueBy',
      header: 'Due by',
      render: (row) => (
        <>
          {row.dueBy ?? '—'}
          {row.isOverdue ? <span className="pill"> Overdue</span> : null}
        </>
      ),
    },
    {
      key: 'sent',
      header: 'Sent',
      render: (row) => {
        if (row.sentAt) {
          return (
            <div className="row">
              <span>
                {row.sentAt.slice(0, 10)} ({row.sentVia ?? '?'})
              </span>
              {row.documentId ? (
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() => void viewNoticeDocument(row.documentId!)}
                >
                  View document
                </button>
              ) : null}
            </div>
          );
        }
        return canWrite ? (
          <div className="row">
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={() => markNoticeSent(row)}
            >
              Mark sent
            </button>
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={() => generateNotice(row)}
            >
              Generate &amp; email (draft)
            </button>
          </div>
        ) : (
          'Not yet'
        );
      },
    },
  ];

  const chargeColumns: Column<ChargeRecord>[] = [
    { key: 'type', header: 'Type', render: (row) => humanizeEnum(row.chargeType) },
    { key: 'due', header: 'Due', render: (row) => row.dueDate ?? '—' },
    { key: 'amount', header: 'Amount', numeric: true, render: (row) => `$${row.amount}` },
    {
      key: 'balance',
      header: 'Balance',
      numeric: true,
      render: (row) => {
        if (row.voidedAt) return <span className="pill">Voided</span>;
        if (row.isPaid) return <span className="pill">Paid</span>;
        return `$${row.balance}`;
      },
    },
    {
      key: 'daysLate',
      header: 'Days late',
      render: (row) => {
        if (row.voidedAt || row.isPaid) return '—';
        return row.daysLate > 0 ? (
          <span className="pill pill--late">{row.daysLate}d late</span>
        ) : (
          <span className="pill">current</span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        canWriteLedger && !row.voidedAt ? (
          <button
            type="button"
            className="button button--quiet"
            disabled={busy}
            onClick={() => voidCharge(row)}
          >
            Void
          </button>
        ) : null,
    },
  ];

  const paymentColumns: Column<PaymentRecord>[] = [
    { key: 'method', header: 'Method', render: (row) => humanizeEnum(row.method) },
    { key: 'received', header: 'Received', render: (row) => row.receivedOn ?? '—' },
    { key: 'amount', header: 'Amount', numeric: true, render: (row) => `$${row.amount}` },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <span className="pill">{humanizeEnum(row.status)}</span>,
    },
    {
      key: 'allocated',
      header: 'Allocated',
      numeric: true,
      render: (row) => `$${row.allocatedTotal}`,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="stack">
          {canWriteLedger && row.status !== 'RETURNED' ? (
            <>
              {Number(row.allocatedTotal) < Number(row.amount) ? (
                <AllocatePanel
                  payment={row}
                  openCharges={openCharges}
                  onSaved={() => void refresh()}
                />
              ) : null}
              <ReversePanel payment={row} onSaved={() => void refresh()} />
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <section className="stack">
      <div className="row">
        <h1>
          {lease.unitLabel} — {lease.propertyName}
        </h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/leases')}
        >
          ← All leases
        </button>
      </div>

      {error ? <p className="alert alert--error">{error}</p> : null}

      <div className="lease-layout">
        <div className="stack">
          <div className="card stack">
            <div className="row">
              <span className={STATUS_PILL_CLASS[lease.status] ?? 'pill'}>
                {humanizeEnum(lease.status)}
              </span>
              <span className="shell__spacer" />
              {canWrite ? (
                <select
                  className="field__input"
                  style={{ width: 'auto' }}
                  value={lease.status}
                  disabled={busy}
                  onChange={(event) => changeStatus(event.target.value)}
                >
                  {LEASE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {humanizeEnum(s)}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>

            <div className="row" style={{ gap: '2rem' }}>
              <div>
                <div className="muted" style={{ fontSize: '0.8rem' }}>
                  Rent
                </div>
                <strong style={{ fontSize: '1.2rem' }}>${lease.monthlyRent}/mo</strong>
              </div>
              <div>
                <div className="muted" style={{ fontSize: '0.8rem' }}>
                  Deposit
                </div>
                <strong style={{ fontSize: '1.2rem' }}>${lease.securityDeposit}</strong>
              </div>
              <div>
                <div className="muted" style={{ fontSize: '0.8rem' }}>
                  Due day
                </div>
                <strong style={{ fontSize: '1.2rem' }}>{lease.rentDueDay}</strong>
              </div>
            </div>

            <p className="muted">
              {lease.startDate ?? '?'} – {lease.endDate ?? 'month-to-month'}
              {lease.depositReceivedOn ? ` · deposit received ${lease.depositReceivedOn}` : ''}
              {lease.moveOutDate ? ` · moved out ${lease.moveOutDate}` : ''}
            </p>
            {lease.notes ? <p>{lease.notes}</p> : null}

            {canWrite && !lease.depositReceivedOn ? (
              <div className="row">
                <button type="button" className="button" disabled={busy} onClick={recordDeposit}>
                  Record deposit received
                </button>
              </div>
            ) : null}

            {canWrite && !lease.moveOutDate ? (
              <MoveOutPanel
                leaseId={id ?? ''}
                onSaved={() => void refresh()}
                onError={(message) => setError(message)}
              />
            ) : null}
          </div>

          <div className="card stack">
            <h2>Tenants</h2>
            <DataTable
              columns={tenantColumns}
              rows={lease.tenants}
              rowKey={(row) => row.id}
              emptyMessage="Nobody is named on this lease."
            />
            {canWrite && addableTenants.length > 0 ? (
              <div className="row">
                <select
                  className="field__input"
                  defaultValue=""
                  disabled={busy}
                  onChange={(event) => {
                    addTenant(event.target.value);
                    event.target.value = '';
                  }}
                >
                  <option value="">Add a tenant to this lease…</option>
                  {addableTenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.firstName} {tenant.lastName}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        </div>

        <ActiveLeasePanel currentLease={lease} />
      </div>

      <div className="card stack">
        <div className="row">
          <h2>Checklists</h2>
          <span className="shell__spacer" />
          {canWrite ? (
            <>
              <button
                type="button"
                className="button button--quiet"
                onClick={() =>
                  void navigate(`/staff/leases/${id ?? ''}/checklists/new?kind=MOVE_IN`)
                }
              >
                New move-in checklist
              </button>
              <button
                type="button"
                className="button button--quiet"
                onClick={() =>
                  void navigate(`/staff/leases/${id ?? ''}/checklists/new?kind=MOVE_OUT`)
                }
              >
                New move-out checklist
              </button>
            </>
          ) : null}
        </div>
        <DataTable
          columns={checklistColumns}
          rows={checklistsData?.data ?? []}
          rowKey={(row) => row.id}
          isLoading={checklistsLoading}
          emptyMessage="No checklists recorded yet."
        />
      </div>

      <div className="card stack">
        <h2>Notices (Michigan)</h2>
        <p className="muted">
          Deposit and damages notices are generated automatically when you record the deposit or the
          move-out above. Staff can attach a real, attorney-reviewed notice under Files and mark it
          sent by hand, or use “Generate &amp; email” below for a quick placeholder draft —{' '}
          <strong>not attorney-reviewed</strong>, useful for testing the send flow, not for a notice
          that actually needs to hold up.
        </p>
        <DataTable
          columns={noticeColumns}
          rows={noticesData?.data ?? []}
          rowKey={(row) => row.id}
          isLoading={noticesLoading}
          emptyMessage="No notices yet."
        />
        {canWrite ? <ManualNoticePanel leaseId={id ?? ''} onSaved={() => void refresh()} /> : null}
      </div>

      <div className="card stack">
        <h2>Ledger</h2>
        <p className="muted">
          Balances are derived from unreversed allocations, never stored — voiding a charge or
          reversing a payment is what the rent roll and delinquency report actually read.
        </p>
        <DataTable
          columns={chargeColumns}
          rows={chargesData?.data ?? []}
          rowKey={(row) => row.id}
          isLoading={chargesLoading}
          emptyMessage="No charges on this lease yet."
        />
        {canWriteLedger ? (
          <AddChargePanel leaseId={id ?? ''} onSaved={() => void refresh()} />
        ) : null}

        <h3>Payments</h3>
        <DataTable
          columns={paymentColumns}
          rows={paymentsData?.data ?? []}
          rowKey={(row) => row.id}
          isLoading={paymentsLoading}
          emptyMessage="No payments recorded yet."
        />
        {canWriteLedger ? (
          <RecordPaymentPanel
            leaseId={id ?? ''}
            tenants={lease.tenants}
            onSaved={() => void refresh()}
          />
        ) : null}
      </div>

      <div className="card stack">
        <h2>Files</h2>
        <DocumentList filter={{ leaseId: lease.id }} canDelete={canWriteDocuments} />
        {canWriteDocuments ? (
          <details>
            <summary>Upload a document</summary>
            <div className="card">
              <PhotoUpload
                parent={{ leaseId: lease.id }}
                defaultDocumentType="LEASE"
                onUploaded={() => {
                  void invalidate({ resource: 'documents', invalidates: ['list'] });
                }}
              />
            </div>
          </details>
        ) : null}
      </div>
    </section>
  );
}

interface UnitLeaseSummary {
  id: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  monthlyRent: string;
  tenants: { name: string; isPrimary: boolean }[];
}

/**
 * Quick context for a lease that might not be the current one for its unit —
 * a DRAFT renewal drafted ahead of a move-out, an ENDED or TERMINATED
 * record being reviewed months later. Reuses the plain lease list filtered
 * to this unit (a unit only ever has a handful of leases) rather than a new
 * endpoint, and picks out whichever one is ACTIVE or MONTH_TO_MONTH now.
 */
function ActiveLeasePanel({ currentLease }: { currentLease: LeaseRecord }) {
  const { data, isLoading } = useList<UnitLeaseSummary>({
    resource: 'leases',
    filters: [{ field: 'unitId', operator: 'eq', value: currentLease.unitId }],
    pagination: { mode: 'off' },
  });

  const thisIsActive = OCCUPIED_STATUSES.has(currentLease.status);
  const activeLease = (data?.data ?? []).find(
    (l) => l.id !== currentLease.id && OCCUPIED_STATUSES.has(l.status),
  );

  return (
    <div className="card stack">
      <h2>Active lease</h2>
      {isLoading ? (
        <p className="muted">Loading…</p>
      ) : thisIsActive ? (
        <p className="muted">This is the active lease for {currentLease.unitLabel}.</p>
      ) : activeLease ? (
        <>
          <p className="muted">{currentLease.unitLabel} is currently under a different lease:</p>
          <div className="row">
            <span className={STATUS_PILL_CLASS[activeLease.status] ?? 'pill'}>
              {humanizeEnum(activeLease.status)}
            </span>
            <span>${activeLease.monthlyRent}/mo</span>
          </div>
          <p className="muted">
            {activeLease.startDate ?? '?'} – {activeLease.endDate ?? 'month-to-month'}
          </p>
          <p>
            {activeLease.tenants.length > 0
              ? activeLease.tenants.map((t) => t.name).join(', ')
              : 'No tenants named'}
          </p>
          <div className="row">
            <Link className="button button--quiet" to={`/staff/leases/${activeLease.id}`}>
              View active lease →
            </Link>
          </div>
        </>
      ) : (
        <p className="muted">{currentLease.unitLabel} has no active lease right now.</p>
      )}
    </div>
  );
}

/**
 * Starts the MCL 554.609 30-day clock — the loudest alert in the application.
 * The forwarding address is optional here (a tenant may not leave one), but the
 * clock runs regardless: missing it forfeits the right to withhold and exposes
 * the company to double damages.
 */
function MoveOutPanel({
  leaseId,
  onSaved,
  onError,
}: {
  leaseId: string;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [moveOutDate, setMoveOutDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('MI');
  const [postalCode, setPostalCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    onError('');
    try {
      await api(`/leases/${leaseId}/move-out`, {
        method: 'POST',
        body: JSON.stringify({
          moveOutDate,
          forwardingAddressLine1: line1 || null,
          forwardingCity: city || null,
          forwardingState: state || null,
          forwardingPostalCode: postalCode || null,
        }),
      });
      onSaved();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Could not record the move-out.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <details>
      <summary>Record move-out</summary>
      <div className="card stack">
        <p className="muted">
          Where the itemized damages notice goes. The tenant may not have left a forwarding address
          — the 30-day clock starts from the move-out date either way.
        </p>
        <div className="row">
          <input
            className="field__input"
            type="date"
            value={moveOutDate}
            onChange={(event) => setMoveOutDate(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="Forwarding street"
            value={line1}
            onChange={(event) => setLine1(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="City"
            value={city}
            onChange={(event) => setCity(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="State"
            value={state}
            onChange={(event) => setState(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="ZIP"
            value={postalCode}
            onChange={(event) => setPostalCode(event.target.value)}
          />
        </div>
        <div className="row">
          <button type="button" className="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Record move-out'}
          </button>
        </div>
      </div>
    </details>
  );
}

/** Lease renewals, rent increases, or anything with no statutory due-date formula. */
function ManualNoticePanel({ leaseId, onSaved }: { leaseId: string; onSaved: () => void }) {
  const manualKinds = NOTICE_KINDS.filter((k) => !AUTOMATIC_NOTICE_KINDS.has(k));
  const [kind, setKind] = useState<string>(manualKinds[0] ?? 'OTHER');
  const [triggeredOn, setTriggeredOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueBy, setDueBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api('/notices', {
        method: 'POST',
        body: JSON.stringify({ leaseId, kind, triggeredOn, dueBy: dueBy || triggeredOn }),
      });
      onSaved();
      setDueBy('');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details>
      <summary>Add a manual notice</summary>
      <div className="card stack">
        <div className="row">
          <select
            className="field__input"
            value={kind}
            onChange={(event) => setKind(event.target.value)}
          >
            {manualKinds.map((k) => (
              <option key={k} value={k}>
                {humanizeEnum(k)}
              </option>
            ))}
          </select>
          <input
            className="field__input"
            type="date"
            value={triggeredOn}
            onChange={(event) => setTriggeredOn(event.target.value)}
          />
          <input
            className="field__input"
            type="date"
            placeholder="Due by"
            value={dueBy}
            onChange={(event) => setDueBy(event.target.value)}
          />
          <button type="button" className="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Add notice'}
          </button>
        </div>
        {error ? (
          <p className="alert alert--error">
            {error instanceof Error ? error.message : 'Could not add that notice.'}
          </p>
        ) : null}
      </div>
    </details>
  );
}

/**
 * An ad-hoc charge — utility, concession, damage, a one-off fee. RENT and
 * LATE_FEE normally come from the monthly job, but nothing here stops staff
 * from entering one by hand (a backdated proration, a corrected amount);
 * `periodStart` stays null either way, so it never collides with the job.
 */
function AddChargePanel({ leaseId, onSaved }: { leaseId: string; onSaved: () => void }) {
  const [chargeType, setChargeType] = useState('OTHER');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api('/charges', {
        method: 'POST',
        body: JSON.stringify({
          leaseId,
          chargeType,
          amount,
          dueDate,
          description: description || null,
        }),
      });
      onSaved();
      setAmount('');
      setDescription('');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details>
      <summary>Add a charge</summary>
      <div className="card stack">
        <div className="row">
          <select
            className="field__input"
            value={chargeType}
            onChange={(event) => setChargeType(event.target.value)}
          >
            {CHARGE_TYPES.map((t) => (
              <option key={t} value={t}>
                {humanizeEnum(t)}
              </option>
            ))}
          </select>
          <input
            className="field__input"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="Amount"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <input
            className="field__input"
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={busy || !amount}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Add charge'}
          </button>
        </div>
        {error ? (
          <p className="alert alert--error">
            {error instanceof Error ? error.message : 'Could not add that charge.'}
          </p>
        ) : null}
      </div>
    </details>
  );
}

/**
 * Manual entry — check, cash, money order. Settles immediately (see the
 * service: no ACH clearing delay for money already in hand), so there's
 * nothing to track between "recorded" and "settled" the way a Stripe payment
 * will need once that path exists.
 */
function RecordPaymentPanel({
  leaseId,
  tenants,
  onSaved,
}: {
  leaseId: string;
  tenants: LeaseTenant[];
  onSaved: () => void;
}) {
  const [method, setMethod] = useState('CHECK');
  const [tenantId, setTenantId] = useState('');
  const [amount, setAmount] = useState('');
  const [receivedOn, setReceivedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api('/payments', {
        method: 'POST',
        body: JSON.stringify({
          leaseId,
          tenantId: tenantId || null,
          method,
          amount,
          receivedOn,
          reference: reference || null,
        }),
      });
      onSaved();
      setAmount('');
      setReference('');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details>
      <summary>Record a payment</summary>
      <div className="card stack">
        <div className="row">
          <select
            className="field__input"
            value={method}
            onChange={(event) => setMethod(event.target.value)}
          >
            {MANUAL_PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {humanizeEnum(m)}
              </option>
            ))}
          </select>
          <select
            className="field__input"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
          >
            <option value="">Unattributed</option>
            {tenants
              .filter((t) => !t.removedOn)
              .map((t) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.name}
                </option>
              ))}
          </select>
          <input
            className="field__input"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="Amount"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <input
            className="field__input"
            type="date"
            value={receivedOn}
            onChange={(event) => setReceivedOn(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="Reference (check #)"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={busy || !amount}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Record payment'}
          </button>
        </div>
        {error ? (
          <p className="alert alert--error">
            {error instanceof Error ? error.message : 'Could not record that payment.'}
          </p>
        ) : null}
      </div>
    </details>
  );
}

/**
 * Apply a payment against open charges. Incremental, matching the service: a
 * payment may be allocated across several visits to this panel as new
 * charges appear, not just once at entry.
 */
function AllocatePanel({
  payment,
  openCharges,
  onSaved,
}: {
  payment: PaymentRecord;
  openCharges: ChargeRecord[];
  onSaved: () => void;
}) {
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const remaining = (Number(payment.amount) - Number(payment.allocatedTotal)).toFixed(2);

  async function save() {
    const allocations = Object.entries(amounts)
      .filter(([, value]) => value && Number(value) > 0)
      .map(([chargeId, value]) => ({ chargeId, amount: value }));
    if (allocations.length === 0) return;

    setBusy(true);
    setError(null);
    try {
      await api(`/payments/${payment.id}/allocate`, {
        method: 'POST',
        body: JSON.stringify({ allocations }),
      });
      onSaved();
      setAmounts({});
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details>
      <summary>Allocate (${remaining} unallocated)</summary>
      <div className="card stack">
        {openCharges.length === 0 ? (
          <p className="muted">No open charges on this lease.</p>
        ) : (
          openCharges.map((charge) => (
            <div key={charge.id} className="row">
              <span>
                {humanizeEnum(charge.chargeType)} — due {charge.dueDate ?? '?'} — balance $
                {charge.balance}
              </span>
              <input
                className="field__input"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amounts[charge.id] ?? ''}
                onChange={(event) =>
                  setAmounts((previous) => ({ ...previous, [charge.id]: event.target.value }))
                }
              />
            </div>
          ))
        )}
        <button type="button" className="button" disabled={busy} onClick={() => void save()}>
          {busy ? 'Allocating…' : 'Allocate'}
        </button>
        {error ? (
          <p className="alert alert--error">
            {error instanceof Error ? error.message : 'Could not allocate that payment.'}
          </p>
        ) : null}
      </div>
    </details>
  );
}

/**
 * A bounced check today; an ACH return arriving as `charge.dispute.created`
 * once the webhook path exists. Every unreversed allocation on the payment is
 * reversed and, unless unchecked, an NSF fee lands on the same lease.
 */
function ReversePanel({ payment, onSaved }: { payment: PaymentRecord; onSaved: () => void }) {
  const [returnedOn, setReturnedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [returnCode, setReturnCode] = useState('');
  const [reason, setReason] = useState('');
  const [assessNsfFee, setAssessNsfFee] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api(`/payments/${payment.id}/reverse`, {
        method: 'POST',
        body: JSON.stringify({
          returnedOn,
          returnCode: returnCode || null,
          reason: reason || null,
          assessNsfFee,
        }),
      });
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details>
      <summary>Reverse</summary>
      <div className="card stack">
        <p className="muted">
          Reverses every unreversed allocation on this payment. Unless unchecked, an NSF fee is
          charged on this lease.
        </p>
        <div className="row">
          <input
            className="field__input"
            type="date"
            value={returnedOn}
            onChange={(event) => setReturnedOn(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="Return code (e.g. R01)"
            value={returnCode}
            onChange={(event) => setReturnCode(event.target.value)}
          />
          <input
            className="field__input"
            placeholder="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <label className="row">
            <input
              type="checkbox"
              checked={assessNsfFee}
              onChange={(event) => setAssessNsfFee(event.target.checked)}
            />
            <span>Assess NSF fee</span>
          </label>
          <button type="button" className="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Reversing…' : 'Reverse payment'}
          </button>
        </div>
        {error ? (
          <p className="alert alert--error">
            {error instanceof Error ? error.message : 'Could not reverse that payment.'}
          </p>
        ) : null}
      </div>
    </details>
  );
}
